import React, { useState, useEffect, useRef } from 'react';
import { 
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wrench,
  Shield,
  Activity,
  Loader2,
  Timer,
  AlertCircle,
  Circle,
  Link,
  Unlink,
  HardDrive,
  Cpu,
  Radio,
  Wifi,
  WifiOff,
  Power,
  PowerOff,
  Terminal,
  Database,
  Server,
  Clock,
  RefreshCw,
  GitBranch,
  GitMerge,
  ChevronRight,
  Flame,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Settings,
  Gauge,
  TrendingUp,
  TrendingDown,
  BarChart3
} from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  type: 'booking' | 'whatsapp' | 'iot' | 'payment' | 'pms';
  status: 'disconnected' | 'connecting' | 'testing' | 'live' | 'error' | 'degraded';
  health: number;
  latency: number;
  throughput: number;
  errors: number;
  uptime: number;
  lastPing: Date;
  credentials?: {
    apiKey?: string;
    secret?: string;
    endpoint?: string;
  };
}

interface TestScenario {
  id: string;
  name: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  steps: TestStep[];
  status: 'pending' | 'running' | 'passed' | 'failed';
  runtime?: number;
}

interface TestStep {
  action: string;
  expected: string;
  actual?: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  duration?: number;
}

interface SystemMetric {
  name: string;
  value: number;
  unit: string;
  threshold: number;
  status: 'good' | 'warning' | 'critical';
}

export default function IntegrationForge() {
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'booking-api',
      name: 'Booking.com',
      type: 'booking',
      status: 'disconnected',
      health: 0,
      latency: 0,
      throughput: 0,
      errors: 0,
      uptime: 0,
      lastPing: new Date()
    },
    {
      id: 'whatsapp-api',
      name: 'WhatsApp Business',
      type: 'whatsapp',
      status: 'disconnected',
      health: 0,
      latency: 0,
      throughput: 0,
      errors: 0,
      uptime: 0,
      lastPing: new Date()
    },
    {
      id: 'iot-sensors',
      name: 'IoT Sensor Network',
      type: 'iot',
      status: 'disconnected',
      health: 0,
      latency: 0,
      throughput: 0,
      errors: 0,
      uptime: 0,
      lastPing: new Date()
    }
  ]);

  const [activeIntegration, setActiveIntegration] = useState<string | null>(null);
  const [testScenarios, setTestScenarios] = useState<TestScenario[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetric[]>([
    { name: 'CPU Load', value: 45, unit: '%', threshold: 80, status: 'good' },
    { name: 'Memory', value: 2.4, unit: 'GB', threshold: 3.5, status: 'good' },
    { name: 'Network I/O', value: 156, unit: 'Mbps', threshold: 100, status: 'warning' },
    { name: 'Disk Queue', value: 12, unit: 'ops', threshold: 50, status: 'good' },
    { name: 'API Calls', value: 8420, unit: '/hr', threshold: 10000, status: 'good' }
  ]);
  
  const [isForging, setIsForging] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [whatIfScenario, setWhatIfScenario] = useState<string>('');
  const [redundancyMode, setRedundancyMode] = useState<'primary' | 'failover'>('primary');
  const terminalRef = useRef<HTMLDivElement>(null);

  const addLog = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = {
      info: '[ INFO ]',
      success: '[  OK  ]',
      warning: '[ WARN ]',
      error: '[ERROR ]'
    }[type];
    
    setLogs(prev => [...prev, `${timestamp} ${prefix} ${message}`].slice(-100));
    
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  };

  const forgeConnection = async (integrationId: string) => {
    setIsForging(true);
    const integration = integrations.find(i => i.id === integrationId);
    if (!integration) return;

    addLog(`INITIATING FORGE SEQUENCE FOR ${integration.name.toUpperCase()}`, 'info');
    
    // Update status to connecting
    setIntegrations(prev => prev.map(i => 
      i.id === integrationId ? { ...i, status: 'connecting' } : i
    ));

    // Simulate connection steps
    const steps = [
      { msg: 'Validating credentials...', delay: 500 },
      { msg: 'Establishing secure tunnel...', delay: 800 },
      { msg: 'Performing SSL handshake...', delay: 600 },
      { msg: 'Authenticating with API...', delay: 1000 },
      { msg: 'Retrieving access tokens...', delay: 700 },
      { msg: 'Testing endpoint connectivity...', delay: 900 },
      { msg: 'Measuring baseline latency...', delay: 500 },
      { msg: 'Calibrating rate limiters...', delay: 600 },
      { msg: 'Initializing health monitors...', delay: 400 },
      { msg: 'Activating redundancy protocols...', delay: 800 }
    ];

    for (const step of steps) {
      await new Promise(resolve => setTimeout(resolve, step.delay));
      addLog(step.msg, 'info');
      
      // Gradually increase health
      setIntegrations(prev => prev.map(i => 
        i.id === integrationId 
          ? { 
              ...i, 
              health: Math.min(100, i.health + 10),
              latency: Math.max(20, 200 - i.health * 1.8),
              throughput: Math.min(1000, i.health * 10)
            } 
          : i
      ));
    }

    // Final connection
    setIntegrations(prev => prev.map(i => 
      i.id === integrationId 
        ? { 
            ...i, 
            status: 'testing',
            health: 100,
            latency: 23,
            throughput: 980,
            uptime: 100,
            lastPing: new Date()
          } 
        : i
    ));

    addLog(`CONNECTION FORGED SUCCESSFULLY!`, 'success');
    addLog(`Running mandatory test suite...`, 'info');

    // Run tests
    await runTestSuite(integrationId);
    
    setIsForging(false);
  };

  const runTestSuite = async (integrationId: string) => {
    const integration = integrations.find(i => i.id === integrationId);
    if (!integration) return;

    const scenarios: TestScenario[] = [
      {
        id: 'auth-test',
        name: 'Authentication Stress Test',
        description: 'Hammer the auth endpoint with 100 rapid requests',
        severity: 'high',
        status: 'pending',
        steps: [
          { action: 'Send invalid credentials', expected: '401 response', status: 'pending' },
          { action: 'Send valid credentials', expected: '200 response + token', status: 'pending' },
          { action: 'Rapid-fire 100 auth requests', expected: 'All handled < 5s', status: 'pending' },
          { action: 'Test token expiration', expected: 'Auto-refresh', status: 'pending' }
        ]
      },
      {
        id: 'load-test',
        name: 'Load Bearing Test',
        description: 'Push connection to 80% capacity',
        severity: 'critical',
        status: 'pending',
        steps: [
          { action: 'Ramp up to 100 req/s', expected: 'Stable latency', status: 'pending' },
          { action: 'Sustain for 30 seconds', expected: 'Zero drops', status: 'pending' },
          { action: 'Spike to 200 req/s', expected: 'Graceful degradation', status: 'pending' },
          { action: 'Return to baseline', expected: 'Full recovery < 10s', status: 'pending' }
        ]
      },
      {
        id: 'failover-test',
        name: 'Failover Simulation',
        description: 'Kill primary connection and test redundancy',
        severity: 'critical',
        status: 'pending',
        steps: [
          { action: 'Establish primary connection', expected: 'Connected', status: 'pending' },
          { action: 'Force disconnect primary', expected: 'Detected < 1s', status: 'pending' },
          { action: 'Activate failover', expected: 'Switched < 3s', status: 'pending' },
          { action: 'Restore primary', expected: 'Seamless return', status: 'pending' }
        ]
      },
      {
        id: 'data-integrity',
        name: 'Data Integrity Check',
        description: 'Verify data consistency across endpoints',
        severity: 'high',
        status: 'pending',
        steps: [
          { action: 'Write test record', expected: 'Confirmation', status: 'pending' },
          { action: 'Read back record', expected: 'Exact match', status: 'pending' },
          { action: 'Concurrent updates', expected: 'No conflicts', status: 'pending' },
          { action: 'Verify audit log', expected: 'Complete trail', status: 'pending' }
        ]
      },
      {
        id: 'edge-cases',
        name: 'Edge Case Battery',
        description: 'Test weird, unexpected scenarios',
        severity: 'medium',
        status: 'pending',
        steps: [
          { action: 'Send 10MB payload', expected: 'Rejected gracefully', status: 'pending' },
          { action: 'Unicode & emoji test', expected: 'Handled correctly', status: 'pending' },
          { action: 'Time zone boundaries', expected: 'Proper conversion', status: 'pending' },
          { action: 'Network interruption', expected: 'Auto-reconnect', status: 'pending' }
        ]
      }
    ];

    setTestScenarios(scenarios);

    // Run each scenario
    for (const scenario of scenarios) {
      addLog(`EXECUTING: ${scenario.name}`, 'warning');
      
      // Update scenario status
      setTestScenarios(prev => prev.map(s => 
        s.id === scenario.id ? { ...s, status: 'running' } : s
      ));

      // Run each step
      for (let stepIndex = 0; stepIndex < scenario.steps.length; stepIndex++) {
        await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 500));
        
        const passed = Math.random() > 0.1; // 90% pass rate
        
        setTestScenarios(prev => prev.map(s => {
          if (s.id === scenario.id) {
            const updatedSteps = [...s.steps];
            updatedSteps[stepIndex] = {
              ...updatedSteps[stepIndex],
              status: passed ? 'passed' : 'failed',
              actual: passed ? updatedSteps[stepIndex].expected : 'FAILURE',
              duration: 200 + Math.random() * 800
            };
            return { ...s, steps: updatedSteps };
          }
          return s;
        }));

        if (!passed) {
          addLog(`FAILED: ${scenario.steps[stepIndex].action}`, 'error');
        }
      }

      // Update scenario final status
      setTestScenarios(prev => prev.map(s => {
        if (s.id === scenario.id) {
          const allPassed = s.steps.every(step => step.status === 'passed');
          addLog(`${scenario.name}: ${allPassed ? 'PASSED' : 'FAILED'}`, allPassed ? 'success' : 'error');
          return { ...s, status: allPassed ? 'passed' : 'failed', runtime: 2000 + Math.random() * 3000 };
        }
        return s;
      }));
    }

    // Update integration to live if all tests pass
    const allPassed = testScenarios.every(s => s.status === 'passed');
    if (allPassed) {
      setIntegrations(prev => prev.map(i => 
        i.id === integrationId ? { ...i, status: 'live' } : i
      ));
      addLog(`${integration.name} IS NOW LIVE AND OPERATIONAL!`, 'success');
    }
  };

  const simulateWhatIf = () => {
    if (!whatIfScenario) return;
    
    addLog(`SIMULATING: ${whatIfScenario}`, 'warning');
    
    // Simulate different scenarios
    if (whatIfScenario.includes('outage')) {
      setIntegrations(prev => prev.map(i => ({
        ...i,
        status: 'error',
        health: 0,
        errors: i.errors + Math.floor(Math.random() * 100)
      })));
      addLog('CRITICAL: All systems experiencing outage!', 'error');
      setTimeout(() => {
        addLog('Initiating disaster recovery protocol...', 'warning');
        setRedundancyMode('failover');
      }, 2000);
    } else if (whatIfScenario.includes('spike')) {
      setSystemMetrics(prev => prev.map(m => ({
        ...m,
        value: m.value * (2 + Math.random()),
        status: m.value > m.threshold ? 'critical' : 'warning'
      })));
      addLog('Traffic spike detected! Scaling horizontally...', 'warning');
    } else if (whatIfScenario.includes('hack')) {
      addLog('SECURITY BREACH DETECTED!', 'error');
      addLog('Isolating affected systems...', 'warning');
      addLog('Rotating all API keys...', 'warning');
      addLog('Notifying security team...', 'info');
    }
  };

  // Real-time monitoring
  useEffect(() => {
    const interval = setInterval(() => {
      setIntegrations(prev => prev.map(i => {
        if (i.status === 'live') {
          return {
            ...i,
            latency: Math.max(10, i.latency + (Math.random() - 0.5) * 10),
            throughput: Math.max(100, i.throughput + (Math.random() - 0.5) * 50),
            errors: Math.random() > 0.95 ? i.errors + 1 : i.errors,
            lastPing: new Date()
          };
        }
        return i;
      }));

      setSystemMetrics(prev => prev.map(m => ({
        ...m,
        value: Math.max(0, m.value + (Math.random() - 0.5) * 5),
        status: m.value > m.threshold ? 'critical' : m.value > m.threshold * 0.8 ? 'warning' : 'good'
      })));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      {/* Header */}
      <div className="mb-6 border-b border-green-400/30 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame className="h-8 w-8 text-orange-500 animate-pulse" />
            <h1 className="text-3xl font-bold text-orange-500">INTEGRATION FORGE</h1>
            <span className="text-sm text-green-400/60">v2.0.3 | BRUTAL MODE</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Radio className={`h-5 w-5 ${redundancyMode === 'primary' ? 'text-green-400' : 'text-yellow-400'} animate-pulse`} />
              <span className="text-sm">{redundancyMode.toUpperCase()} NODE</span>
            </div>
            <button
              onClick={() => setRedundancyMode(redundancyMode === 'primary' ? 'failover' : 'primary')}
              className="px-3 py-1 border border-green-400/50 hover:bg-green-400/10 transition-colors"
            >
              SWITCH NODE
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Integration List */}
        <div className="col-span-3 space-y-3">
          <h2 className="text-lg font-bold mb-3 text-green-400">AVAILABLE CONNECTIONS</h2>
          {integrations.map(integration => (
            <div
              key={integration.id}
              className={`border p-3 cursor-pointer transition-all ${
                activeIntegration === integration.id 
                  ? 'border-orange-500 bg-orange-500/10' 
                  : 'border-green-400/30 hover:border-green-400/60'
              }`}
              onClick={() => setActiveIntegration(integration.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold">{integration.name}</span>
                <div className="flex items-center gap-1">
                  {integration.status === 'disconnected' && <WifiOff className="h-4 w-4 text-red-500" />}
                  {integration.status === 'connecting' && <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />}
                  {integration.status === 'testing' && <Activity className="h-4 w-4 text-blue-400 animate-pulse" />}
                  {integration.status === 'live' && <Wifi className="h-4 w-4 text-green-400" />}
                  {integration.status === 'error' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                </div>
              </div>
              
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>HEALTH:</span>
                  <span className={integration.health > 80 ? 'text-green-400' : integration.health > 50 ? 'text-yellow-400' : 'text-red-500'}>
                    {integration.health}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>LATENCY:</span>
                  <span>{integration.latency}ms</span>
                </div>
                <div className="flex justify-between">
                  <span>THROUGHPUT:</span>
                  <span>{integration.throughput} req/s</span>
                </div>
                {integration.errors > 0 && (
                  <div className="flex justify-between text-red-500">
                    <span>ERRORS:</span>
                    <span>{integration.errors}</span>
                  </div>
                )}
              </div>

              {integration.status === 'disconnected' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    forgeConnection(integration.id);
                  }}
                  disabled={isForging}
                  className="w-full mt-3 py-1 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:bg-gray-700 disabled:text-gray-500 transition-colors"
                >
                  {isForging ? 'FORGING...' : 'FORGE CONNECTION'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Main Panel */}
        <div className="col-span-6 space-y-4">
          {/* System Metrics */}
          <div className="border border-green-400/30 p-4">
            <h2 className="text-lg font-bold mb-3 text-green-400">SYSTEM METRICS</h2>
            <div className="grid grid-cols-5 gap-3">
              {systemMetrics.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className={`text-2xl font-bold ${
                    metric.status === 'good' ? 'text-green-400' :
                    metric.status === 'warning' ? 'text-yellow-400' :
                    'text-red-500'
                  }`}>
                    {metric.value.toFixed(0)}
                  </div>
                  <div className="text-xs text-green-400/60">{metric.unit}</div>
                  <div className="text-xs">{metric.name}</div>
                  <div className="h-1 bg-gray-800 mt-1">
                    <div 
                      className={`h-1 ${
                        metric.status === 'good' ? 'bg-green-400' :
                        metric.status === 'warning' ? 'bg-yellow-400' :
                        'bg-red-500'
                      }`}
                      style={{width: `${(metric.value / metric.threshold) * 100}%`}}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Test Results */}
          {testScenarios.length > 0 && (
            <div className="border border-green-400/30 p-4">
              <h2 className="text-lg font-bold mb-3 text-green-400">TEST SCENARIOS</h2>
              <div className="space-y-2">
                {testScenarios.map(scenario => (
                  <div key={scenario.id} className="border border-green-400/20 p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{scenario.name}</span>
                        <span className={`text-xs px-2 py-0.5 ${
                          scenario.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                          scenario.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          scenario.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {scenario.severity.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {scenario.status === 'pending' && <Circle className="h-4 w-4 text-gray-500" />}
                        {scenario.status === 'running' && <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />}
                        {scenario.status === 'passed' && <CheckCircle className="h-4 w-4 text-green-400" />}
                        {scenario.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                        {scenario.runtime && (
                          <span className="text-xs text-green-400/60">{(scenario.runtime / 1000).toFixed(1)}s</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-green-400/60 mb-2">{scenario.description}</div>
                    <div className="space-y-1">
                      {scenario.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="flex items-center gap-2 text-xs">
                          {step.status === 'pending' && <Circle className="h-3 w-3 text-gray-500" />}
                          {step.status === 'running' && <Loader2 className="h-3 w-3 text-yellow-400 animate-spin" />}
                          {step.status === 'passed' && <CheckCircle className="h-3 w-3 text-green-400" />}
                          {step.status === 'failed' && <XCircle className="h-3 w-3 text-red-500" />}
                          <span className={step.status === 'failed' ? 'text-red-400' : ''}>{step.action}</span>
                          {step.duration && <span className="text-green-400/40">({step.duration.toFixed(0)}ms)</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What-If Simulator */}
          <div className="border border-green-400/30 p-4">
            <h2 className="text-lg font-bold mb-3 text-green-400">WHAT-IF SCENARIO</h2>
            <div className="flex gap-2">
              <input
                type="text"
                value={whatIfScenario}
                onChange={(e) => setWhatIfScenario(e.target.value)}
                placeholder="Type scenario (e.g., 'total outage', 'traffic spike', 'security hack')"
                className="flex-1 bg-black border border-green-400/30 px-3 py-2 text-green-400 placeholder-green-400/30 focus:border-orange-500 focus:outline-none"
              />
              <button
                onClick={simulateWhatIf}
                className="px-4 py-2 bg-red-500 text-black font-bold hover:bg-red-400 transition-colors"
              >
                SIMULATE
              </button>
            </div>
          </div>
        </div>

        {/* Terminal */}
        <div className="col-span-3">
          <div className="border border-green-400/30 h-full flex flex-col">
            <div className="bg-green-400/10 px-3 py-2 border-b border-green-400/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Terminal className="h-4 w-4" />
                <span className="font-bold">SYSTEM LOG</span>
              </div>
              <button
                onClick={() => setLogs([])}
                className="text-xs text-green-400/60 hover:text-green-400"
              >
                CLEAR
              </button>
            </div>
            <div 
              ref={terminalRef}
              className="flex-1 p-3 overflow-y-auto text-xs space-y-1"
              style={{ maxHeight: '600px' }}
            >
              {logs.map((log, index) => (
                <div 
                  key={index} 
                  className={
                    log.includes('[ERROR ]') ? 'text-red-400' :
                    log.includes('[ WARN ]') ? 'text-yellow-400' :
                    log.includes('[  OK  ]') ? 'text-green-400' :
                    'text-green-400/80'
                  }
                >
                  {log}
                </div>
              ))}
              <div className="animate-pulse">_</div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="mt-6 border-t border-green-400/30 pt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <span>CONNECTIONS: {integrations.filter(i => i.status === 'live').length}/{integrations.length}</span>
          <span>UPTIME: 99.97%</span>
          <span>TOTAL REQUESTS: 1,247,892</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-yellow-400">⚠ 3 WARNINGS</span>
          <span className="text-green-400">✓ REDUNDANCY ACTIVE</span>
          <span className="text-orange-400 animate-pulse">● FORGE READY</span>
        </div>
      </div>
    </div>
  );
}